/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useEffect, useRef, useState } from "react"
import { useField } from "formik"
import { format } from "date-fns"
import { CalendarIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline"
import { classNames } from "@medaica/common/services/util"
import { bookingColors } from "../booking-theme"

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]
// The calendar opens here when the field is empty — most patients' births sit decades back, so
// starting at 1960 beats paging back from the current year.
const DEFAULT_YEAR = 1960
const DEFAULT_MONTH = 0 // January
const MIN_YEAR = 1900
const YEARS_PER_PAGE = 12

// First year of the 12-year page containing `year` (pages aligned to MIN_YEAR).
const pageStartFor = (year: number): number =>
  MIN_YEAR + Math.floor((year - MIN_YEAR) / YEARS_PER_PAGE) * YEARS_PER_PAGE

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max)

type View = "days" | "months" | "years"
type Parsed = { year: number; month: number; day: number } // month is 1-12

// Prev/next control config for the current view — the ‹ › arrows step the "coarser" unit (month in
// day view, year in month view, a decade in year view). Kept as a per-view lookup so the header row
// stays free of branching.
type NavConfig = {
  onPrev: () => void
  onNext: () => void
  prevDisabled: boolean
  nextDisabled: boolean
  prevLabel: string
  nextLabel: string
}

// Parse the stored "YYYY-MM-DD" (same format the native date input produced) into local parts.
const parseValue = (value: string): Parsed | null => {
  if (!value) {
    return null
  }
  const parts = value.split("-").map(Number)
  if (parts.length !== 3 || parts.some(Number.isNaN)) {
    return null
  }
  const [year, month, day] = parts
  return { year, month, day }
}

const pad = (n: number): string => String(n).padStart(2, "0")

// Advance a month by ±1, rolling the year at the boundaries. Returns null when the roll would leave
// the supported [MIN_YEAR, maxYear] range, so the caller can no-op instead of moving out of bounds.
const stepMonthState = (
  month: number,
  year: number,
  delta: number,
  maxYear: number
): { month: number; year: number } | null => {
  let nextMonth = month + delta
  let nextYear = year
  if (nextMonth < 0) {
    nextMonth = 11
    nextYear -= 1
  } else if (nextMonth > 11) {
    nextMonth = 0
    nextYear += 1
  }
  if (nextYear < MIN_YEAR || nextYear > maxYear) {
    return null
  }
  return { month: nextMonth, year: nextYear }
}

// Header row: ‹ prev, the month button (opens the month grid), the year button (opens the year grid),
// next ›. Presentation only — every handler/label is supplied by the parent.
const CalendarNav = ({
  view,
  viewMonth,
  viewYear,
  nav,
  onToggleMonth,
  onToggleYear,
}: {
  view: View
  viewMonth: number
  viewYear: number
  nav: NavConfig
  onToggleMonth: () => void
  onToggleYear: () => void
}): ReactElement => (
  <div className="mb-2 flex items-center gap-2">
    <button
      type="button"
      onClick={nav.onPrev}
      disabled={nav.prevDisabled}
      aria-label={nav.prevLabel}
      className="flex-shrink-0 rounded p-1 text-gray-600 hover:bg-gray-100 disabled:opacity-40"
    >
      <ChevronLeftIcon className="h-5 w-5" />
    </button>
    <button
      type="button"
      onClick={onToggleMonth}
      aria-label="Select month"
      aria-expanded={view === "months"}
      className="input flex min-w-0 flex-1 items-center justify-between py-1 pl-2 pr-2 text-sm text-gray-900"
    >
      <span className="truncate">{MONTHS[viewMonth]}</span>
      <ChevronDownIcon
        className={classNames("h-4 w-4 flex-shrink-0 text-gray-500", view === "months" ? "rotate-180" : "")}
        aria-hidden="true"
      />
    </button>
    <button
      type="button"
      onClick={onToggleYear}
      aria-label="Select year"
      aria-expanded={view === "years"}
      className="input flex flex-shrink-0 items-center gap-1 py-1 pl-2 pr-2 text-sm text-gray-900"
    >
      {viewYear}
      <ChevronDownIcon
        className={classNames("h-4 w-4 text-gray-500", view === "years" ? "rotate-180" : "")}
        aria-hidden="true"
      />
    </button>
    <button
      type="button"
      onClick={nav.onNext}
      disabled={nav.nextDisabled}
      aria-label={nav.nextLabel}
      className="flex-shrink-0 rounded p-1 text-gray-600 hover:bg-gray-100 disabled:opacity-40"
    >
      <ChevronRightIcon className="h-5 w-5" />
    </button>
  </div>
)

const MonthGrid = ({ viewMonth, onPick }: { viewMonth: number; onPick: (month: number) => void }): ReactElement => (
  <div className="grid grid-cols-3 gap-1">
    {MONTHS.map((monthName, i) => (
      <button
        key={monthName}
        type="button"
        onClick={() => onPick(i)}
        aria-label={monthName}
        aria-pressed={i === viewMonth}
        className={classNames(
          "rounded-md py-3 text-sm",
          i === viewMonth ? "text-white" : "text-gray-800 hover:bg-gray-100"
        )}
        style={i === viewMonth ? { backgroundColor: bookingColors.primary } : undefined}
      >
        {monthName.slice(0, 3)}
      </button>
    ))}
  </div>
)

const YearGrid = ({
  yearPageStart,
  viewYear,
  currentYear,
  onPick,
}: {
  yearPageStart: number
  viewYear: number
  currentYear: number
  onPick: (year: number) => void
}): ReactElement => (
  <div className="grid grid-cols-4 gap-1">
    {Array.from({ length: YEARS_PER_PAGE }, (_, i) => yearPageStart + i).map((y) =>
      y < MIN_YEAR || y > currentYear ? (
        <div key={y} />
      ) : (
        <button
          key={y}
          type="button"
          onClick={() => onPick(y)}
          aria-label={`Year ${y}`}
          aria-pressed={y === viewYear}
          className={classNames(
            "rounded-md py-3 text-sm",
            y === viewYear ? "text-white" : "text-gray-800 hover:bg-gray-100"
          )}
          style={y === viewYear ? { backgroundColor: bookingColors.primary } : undefined}
        >
          {y}
        </button>
      )
    )}
  </div>
)

const DayGrid = ({
  viewYear,
  viewMonth,
  isSelected,
  onSelect,
}: {
  viewYear: number
  viewMonth: number
  isSelected: (day: number) => boolean
  onSelect: (day: number) => void
}): ReactElement => {
  const firstWeekday = new Date(viewYear, viewMonth, 1).getDay()
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
  // Leading blanks fill the days before the 1st; key them by the weekday they occupy (stable, unique)
  // rather than a loop index.
  const leadingBlanks = WEEKDAYS.slice(0, firstWeekday)
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1)
  return (
    <>
      <div className="mb-1 grid grid-cols-7 text-center text-xs text-gray-500">
        {WEEKDAYS.map((weekday) => (
          <div key={weekday} className="py-1">
            {weekday}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {leadingBlanks.map((weekday) => (
          <div key={`blank-${weekday}`} />
        ))}
        {days.map((day) => (
          <button
            key={day}
            type="button"
            onClick={() => onSelect(day)}
            aria-label={`${MONTHS[viewMonth]} ${day}, ${viewYear}`}
            aria-pressed={isSelected(day)}
            className={classNames(
              "flex h-8 w-8 items-center justify-center rounded-md text-sm",
              isSelected(day) ? "text-white" : "text-gray-800 hover:bg-gray-100"
            )}
            style={isSelected(day) ? { backgroundColor: bookingColors.primary } : undefined}
          >
            {day}
          </button>
        ))}
      </div>
    </>
  )
}

/**
 * Custom date-of-birth calendar (05 §4 redesign). A `.input`-styled trigger opens a popover with a
 * day grid; tapping the month or the year swaps it for a month grid / year grid (paged by decade),
 * so neither field falls back to a native <select>'s clunky OS overlay. It writes the SAME
 * "YYYY-MM-DD" string the native `<input type="date">` produced — validation/submission unchanged.
 */
const DateOfBirthField = ({
  name,
  label,
  placeholder,
  className,
}: {
  name: string
  label: string
  placeholder: string
  // Extra classes for the trigger. Buttons don't receive @tailwindcss/forms' input padding
  // (it targets input/select elements), so the caller passes the skin that keeps this
  // trigger pixel-identical to the neighboring text inputs.
  className?: string
}): ReactElement => {
  const [field, meta, helpers] = useField<string>(name)
  const parsed = parseValue(field.value)
  const showError = meta.touched && !!meta.error

  // Seed the view from the current value, else the 1960 default.
  const initialView = parsed ?? { year: DEFAULT_YEAR, month: DEFAULT_MONTH + 1, day: 1 }
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<View>("days")
  const [viewYear, setViewYear] = useState(initialView.year)
  const [viewMonth, setViewMonth] = useState(initialView.month - 1)
  const [yearPageStart, setYearPageStart] = useState(pageStartFor(initialView.year))
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const currentYear = new Date().getFullYear()
  const maxPageStart = pageStartFor(currentYear)

  const close = (): void => {
    setOpen(false)
    void helpers.setTouched(true)
  }

  // Sync the view to the current value (or the 1960 default) whenever the popover opens.
  const openCalendar = (): void => {
    const p = parseValue(field.value) ?? { year: DEFAULT_YEAR, month: DEFAULT_MONTH + 1, day: 1 }
    setViewYear(p.year)
    setViewMonth(p.month - 1)
    setView("days")
    setOpen(true)
  }

  useEffect(() => {
    if (!open) {
      return
    }
    const onPointerDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        close()
      }
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        close()
        triggerRef.current?.focus() // Escape returns focus to the trigger (WCAG 2.4.3)
      }
    }
    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const selectDay = (day: number): void => {
    // Validate on setValue (with the new value); mark touched WITHOUT re-validating, or Formik
    // re-runs validation against the pre-commit (empty) value and flashes a stale "Required".
    void helpers.setTouched(true, false)
    void helpers.setValue(`${viewYear}-${pad(viewMonth + 1)}-${pad(day)}`, true)
    setOpen(false)
    triggerRef.current?.focus() // return focus to the trigger so it isn't dropped to <body> on close
  }

  const stepMonth = (delta: number): void => {
    const next = stepMonthState(viewMonth, viewYear, delta, currentYear)
    if (next) {
      setViewMonth(next.month)
      setViewYear(next.year)
    }
  }

  const stepYear = (delta: number): void => {
    setViewYear((y) => clamp(y + delta, MIN_YEAR, currentYear))
  }

  const pageYears = (delta: number): void => {
    setYearPageStart((start) => clamp(start + delta * YEARS_PER_PAGE, MIN_YEAR, maxPageStart))
  }

  const pickMonth = (month: number): void => {
    setViewMonth(month)
    setView("days")
  }

  const pickYear = (year: number): void => {
    setViewYear(year)
    setView("days")
  }

  const openYearView = (): void => {
    setYearPageStart(pageStartFor(viewYear))
    setView("years")
  }

  const isSelected = (day: number): boolean =>
    !!parsed && parsed.year === viewYear && parsed.month - 1 === viewMonth && parsed.day === day

  const displayText = parsed ? format(new Date(parsed.year, parsed.month - 1, parsed.day), "d MMM yyyy") : ""

  // ‹ › step the coarser unit for the current view; the header buttons swap the day grid for the
  // month/year grid. Kept as lookups so the render below has no nested branching.
  const navByView: Record<View, NavConfig> = {
    years: {
      onPrev: () => pageYears(-1),
      onNext: () => pageYears(1),
      prevDisabled: yearPageStart <= MIN_YEAR,
      nextDisabled: yearPageStart >= maxPageStart,
      prevLabel: "Previous years",
      nextLabel: "Next years",
    },
    months: {
      onPrev: () => stepYear(-1),
      onNext: () => stepYear(1),
      prevDisabled: viewYear <= MIN_YEAR,
      nextDisabled: viewYear >= currentYear,
      prevLabel: "Previous year",
      nextLabel: "Next year",
    },
    days: {
      onPrev: () => stepMonth(-1),
      onNext: () => stepMonth(1),
      prevDisabled: false,
      nextDisabled: false,
      prevLabel: "Previous month",
      nextLabel: "Next month",
    },
  }

  const gridByView: Record<View, ReactElement> = {
    months: <MonthGrid viewMonth={viewMonth} onPick={pickMonth} />,
    years: <YearGrid yearPageStart={yearPageStart} viewYear={viewYear} currentYear={currentYear} onPick={pickYear} />,
    days: <DayGrid viewYear={viewYear} viewMonth={viewMonth} isSelected={isSelected} onSelect={selectDay} />,
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="block font-base text-gray-700">{label}</label>
      <div className="mt-1">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => (open ? close() : openCalendar())}
          className={classNames(
            "input flex w-full items-center justify-between text-left",
            className,
            showError ? "input-error" : ""
          )}
          aria-expanded={open}
          aria-describedby={showError ? `${name}-error` : undefined}
        >
          <span className={displayText ? "text-gray-900" : "text-gray-500"}>{displayText || placeholder}</span>
          <CalendarIcon className="h-5 w-5 flex-shrink-0 text-gray-500" aria-hidden="true" />
        </button>
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-80 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
          {/* Month and year are both buttons that open a contained grid (no native <select> overlay);
              ‹ › navigate the coarser unit for the current view. */}
          <CalendarNav
            view={view}
            viewMonth={viewMonth}
            viewYear={viewYear}
            nav={navByView[view]}
            onToggleMonth={() => setView(view === "months" ? "days" : "months")}
            onToggleYear={() => (view === "years" ? setView("days") : openYearView())}
          />
          {gridByView[view]}
        </div>
      )}

      {showError && (
        <div id={`${name}-error`} role="alert" className="mt-1 text-error">
          {meta.error}
        </div>
      )}
    </div>
  )
}

export default DateOfBirthField
